import { Card,CardContent,Stack } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React,{ useEffect,useState } from "react";
import { useAuth } from "../contexts/auth";
import { FileObjectWithSource,getDirectoriesForPath } from "../data-access/directory";
import { DisplayMode } from "../models/DisplayMode";
import DisplayGrid from "./DisplayGrid";
import DisplayTable from "./DisplayTable";
import NavBreadcrumb from "./NavBreadcrumb";
import RichTextDisplay from "./RichTextDisplay";
import SubcategoryTiles from "./SubcategoryTiles";

interface DocsDisplayProps {
  url: string;
  defaultDisplayMode: DisplayMode;
}
export const DocsDisplay: React.FC<DocsDisplayProps> = ({ url, defaultDisplayMode }) => {
  const [displayMode] = useState<DisplayMode>(defaultDisplayMode);
  const [loading, setLoading] = useState<boolean>(true);
  const [coreItems, setCoreItems] = useState<FileObjectWithSource[] | undefined>();
  const [userItems, setUserItems] = useState<FileObjectWithSource[] | null>();
  const [isLeafNode, setIsLeafNode] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadVersion, setReloadVersion] = useState(0);
  const ref = React.useRef<HTMLInputElement>(null);
  const auth = useAuth();
  const selectedCompanyId = auth.selectedCompany?.id;

  useEffect(() => {
    if (ref.current !== null) {
      ref.current.setAttribute("directory", "");
      ref.current.setAttribute("webkitdirectory", "");
      ref.current.setAttribute("mozdirectory", "");
    }
  }, [ref]);

  useEffect(() => {
    let isCurrent = true;

    async function loadData() {
      if (!selectedCompanyId) {
        setError(null);
        setCoreItems(undefined);
        setUserItems(null);
        setIsLeafNode(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setCoreItems(undefined);
      setUserItems(null);
      setIsLeafNode(false);

      try {
        const fetchedDirs = await getDirectoriesForPath(url, selectedCompanyId);
        if (!isCurrent) return;

        if (fetchedDirs != null) {
          setCoreItems(fetchedDirs.coreData);
          setUserItems(fetchedDirs.userData);
          setIsLeafNode(fetchedDirs.isLeafNode);
        }
      } catch (err) {
        if (!isCurrent) return;

        console.error(err);
        setError("Unable to load this directory.");
      } finally {
        if (isCurrent) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      isCurrent = false;
    };
  }, [url, selectedCompanyId, reloadVersion]);

  function reloadData() {
    setReloadVersion((version) => version + 1);
  }


  const displayCoreItems = (coreItems && coreItems.length > 0) || auth.isAdmin;

  const pathSegments = url.split("/").filter(x => x != null && x !== "");

  let readPermission =
    auth?.permissions?.find((x) => x.path === pathSegments[0] && x.read_permission) != null;
  let writePermission =
    auth?.permissions?.find((x) => x.path === pathSegments[0] && x.write_permission) != null;

  if (auth?.isAdmin) {
    readPermission = true;
    writePermission = true;
  }

  return (
    <Stack direction={"column"} gap={3}>
      <Card>
        <CardContent>
          <NavBreadcrumb></NavBreadcrumb>
          <RichTextDisplay path={url}></RichTextDisplay>
        </CardContent>
      </Card>
      {loading && <CircularProgress></CircularProgress>}
      {!loading && error && <p role="alert">{error}</p>}
      {!loading && displayCoreItems && (
        <SubcategoryTiles
          dirs={coreItems}
          baseUrl={url.split("/")}
          reloadData={reloadData}
        ></SubcategoryTiles>
      )}
      {!loading && displayMode === DisplayMode.Grid && userItems && (
        <DisplayGrid dirs={userItems} path={url}></DisplayGrid>
      )}
      {!loading && displayMode === DisplayMode.Table && userItems && (
        <>
          {!readPermission && <h4>No access to view files</h4>}
          {readPermission && (
            <DisplayTable
              dirs={userItems}
              path={pathSegments}
              reloadData={reloadData}
              isLeafNode={isLeafNode}
              readPermission={readPermission}
              writePermission={writePermission}
            ></DisplayTable>
          )}
        </>
      )}
    </Stack>
  );
};
